import { ForecastPerWeek } from '@domain/inventory/models/ForecastPerWeek';
import { ProductInventory } from '@domain/inventory/models/Inventory';
import { ProductSales } from '@domain/sales/models/Sales';
import { isWeekInRange, weekStartDate } from './periods';

const weekOutboundQty = (week: ForecastPerWeek): number => {
  const weekQty = (week.outbounds ?? []).reduce(
    (sum, outbound) => sum + (outbound.quantity ?? 0),
    0
  );
  if (
    weekQty === 0 &&
    week.startInventory != null &&
    week.endInventory != null
  ) {
    return Math.max(0, week.startInventory - week.endInventory);
  }
  return weekQty;
};

export const sumForecastInRange = (
  item: ProductInventory,
  start: Date,
  end: Date
): number =>
  (item.forecastPerWeek ?? [])
    .filter((week) => isWeekInRange(week.year, week.week, start, end))
    .reduce((sum, week) => sum + weekOutboundQty(week), 0);

/** Sum actual sales for weeks in range that have already started. */
export const sumActualSalesInRange = (
  productSales: ProductSales | undefined,
  start: Date,
  end: Date,
  today: Date = new Date()
): number | null => {
  if (!productSales?.weeks?.length) return null;

  let total = 0;
  let hasActualWeek = false;

  for (const week of productSales.weeks) {
    if (!isWeekInRange(week.year, week.week, start, end)) continue;

    const weekStart = weekStartDate(week.year, week.week);
    if (weekStart > today) continue;

    hasActualWeek = true;
    total += week.sales.quantity ?? 0;
  }

  return hasActualWeek ? total : null;
};

/** Forecast for weeks in range that have not started yet. */
export const sumRemainingForecastInRange = (
  item: ProductInventory,
  start: Date,
  end: Date,
  today: Date = new Date()
): number =>
  (item.forecastPerWeek ?? [])
    .filter((week) => {
      if (!isWeekInRange(week.year, week.week, start, end)) return false;
      return weekStartDate(week.year, week.week) > today;
    })
    .reduce((sum, week) => sum + weekOutboundQty(week), 0);

/**
 * Full period outlook: actual sales for weeks that have started
 * plus inventory forecast for weeks still ahead.
 */
export const sumCompleteForecastInRange = (
  item: ProductInventory,
  productSales: ProductSales | undefined,
  start: Date,
  end: Date,
  today: Date = new Date()
): number => {
  const actual = sumActualSalesInRange(productSales, start, end, today) ?? 0;
  const remaining = sumRemainingForecastInRange(item, start, end, today);
  return actual + remaining;
};

/** Half-over-half change within the selected period's forecast weeks. */
export const forecastChangePctInRange = (
  item: ProductInventory,
  start: Date,
  end: Date
): number | null => {
  const weeks = (item.forecastPerWeek ?? []).filter((week) =>
    isWeekInRange(week.year, week.week, start, end)
  );
  if (weeks.length < 2) return null;

  const half = Math.max(1, Math.floor(weeks.length / 2));
  const firstHalf = weeks
    .slice(0, half)
    .reduce((sum, week) => sum + weekOutboundQty(week), 0);
  const secondHalf = weeks
    .slice(half, half * 2)
    .reduce((sum, week) => sum + weekOutboundQty(week), 0);

  if (firstHalf <= 0) return secondHalf > 0 ? 100 : null;
  return ((secondHalf - firstHalf) / firstHalf) * 100;
};
